import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  formatDateMMMddYYYY,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { API_ENDPOINT } from "../../utils/constants";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import CustomCircularP from "../../widgets/CustomCircularP";
import axios from "axios";
import { toast } from "react-toastify";
import AddSupplierItem from "./AddSupplierItem";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";

const SupplierItems = () => {
  const [reloadItems, setReloadItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const {
    data: formItems,
    isLoading,
    isError,
    error,
  } = useApiEp(`/admin/get-suppliers?reload=${reloadItems}`);
  const [actionMode, setActionMode] = useState("add");
  const [showDeleteItem, setShowDeleteItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null); // State to control menu
  const [selectedItem, setSelectedItem] = useState(null); // Track selected item
  const open = Boolean(anchorEl);
  const filteredItems = (formItems || []).filter((item) =>
    item.supplierName?.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleEditItem = (item) => {
    setActionMode("edit");
    setCurrentItem(item);
    handleMenuClose();
    setShowAddSupplier((prev) => !prev);
  };

  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    handleMenuClose();
    setShowDeleteItem((prev) => !prev);
  };
  const handleAddItem = () => {
    setActionMode("add");
    setShowAddSupplier((prev) => !prev);
  };
  const onAddedItem = () => {
    setShowAddSupplier((prev) => !prev);
    setReloadItems((prev) => !prev);
  };
  const onYesDelete = async () => {
    setShowDeleteItem((prev) => !prev);
    if (!currentItem) {
      return;
    }
    try {
      setDeletingItem(true);
      const { data } = await axios.delete(
        `${API_ENDPOINT}/admin/delete-supplier?id=${currentItem._id}`,
        getHeader()
      );
      setDeletingItem(false);
      toast.success(data.message);
      setReloadItems((prev) => !prev);
    } catch (error) {
      toastError(error);
    } finally {
      setDeletingItem(false);
    }
  };

  return (
    <Box>
      <CustomCircularP show={deletingItem} />
      <SimpleYesNoDialog
        title="Please confirm!"
        message="Are you sure you want to delete this?"
        show={showDeleteItem}
        setShow={setShowDeleteItem}
        onYes={onYesDelete}
      />
      <RdMyModal
        mWidth={"720px"}
        showModal={showAddSupplier}
        setShowModal={setShowAddSupplier}
        modalC={
          <AddSupplierItem
            onAdded={onAddedItem}
            show={showAddSupplier}
            mode={actionMode}
            editItem={currentItem}
          />
        }
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2.5, justifyContent: "space-between" }}
      >
        <TextField
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search suppliers by name..."
          size="small"
          inputProps={{ "aria-label": "Search suppliers by name" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
              <SearchIcon sx={{ color: "#7c3aed" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 340 },
            "& .MuiOutlinedInput-root": {
              height: 42,
              bgcolor: "#fff",
              borderRadius: 2,
              fontSize: "0.875rem",
            },
          }}
        />
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Box
            sx={{
              display: "flex",
              bgcolor: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <IconButton
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
              aria-pressed={viewMode === "grid"}
              sx={{
                width: 40,
                borderRadius: 0,
                color: viewMode === "grid" ? "#7c3aed" : "#94a3b8",
                bgcolor: viewMode === "grid" ? "#f5f3ff" : "transparent",
              }}
            >
              <GridViewRoundedIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode("list")}
              aria-label="List view"
              aria-pressed={viewMode === "list"}
              sx={{
                width: 40,
                borderLeft: "1px solid #cbd5e1",
                borderRadius: 0,
                color: viewMode === "list" ? "#7c3aed" : "#94a3b8",
                bgcolor: viewMode === "list" ? "#f5f3ff" : "transparent",
              }}
            >
              <ViewListRoundedIcon />
            </IconButton>
          </Box>
          <Button
            onClick={handleAddItem}
            startIcon={<AddIcon />}
            variant="contained"
            sx={{
              minHeight: 42,
              px: 2,
              borderRadius: 2,
              boxShadow: "none",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              bgcolor: "#7c3aed",
              "&:hover": { bgcolor: "#6d28d9", boxShadow: "none" },
            }}
          >
            Add Supplier
          </Button>
        </Stack>
      </Stack>
      {filteredItems.length === 0 && (
        <Typography sx={{ py: 8, textAlign: "center", color: "#94a3b8" }}>
          {searchTerm ? "No suppliers match your search" : "No suppliers yet"}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            viewMode === "grid"
              ? {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                }
              : "1fr",
          gap: 2,
        }}
      >
        {filteredItems.map((item, index) => (
        <Card
          key={item._id || index}
          sx={{
            position: "relative",
            minHeight: viewMode === "grid" ? 220 : 128,
            display: "flex",
            border: "1px solid #dbe3ef",
            borderTop: "3px solid #7c3aed",
            borderRadius: 2,
            boxShadow: "0 3px 12px rgba(15, 23, 42, 0.04)",
            overflow: "visible",
          }}
        >
          <CardContent
            sx={{
              width: "100%",
              p: { xs: 2, sm: 2.25 },
              pb: "18px !important",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                bgcolor: "#f5f3ff",
                border: "1px solid #ddd6fe",
                color: "#7c3aed",
                mb: 1.5,
              }}
            >
              <StorefrontRoundedIcon sx={{ fontSize: 21 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "0.98rem",
                lineHeight: 1.35,
                fontWeight: 700,
                pr: 4,
              }}
            >
              {item.supplierName}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                alignSelf: "flex-start",
                gap: 0.75,
                mt: 1.25,
                px: 1,
                py: 0.35,
                borderRadius: 10,
                bgcolor: "#f1f5f9",
                color: "#64748b",
              }}
            >
              <CalendarTodayRoundedIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: "0.78rem" }}>
                Added: {formatDateMMMddYYYY(item.createdOn)}
              </Typography>
            </Box>
            <Button
              onClick={() => handleEditItem(item)}
              startIcon={<EditIcon />}
              fullWidth
              sx={{
                mt: "auto",
                pt: viewMode === "grid" ? 0.75 : 1.5,
                minHeight: 38,
                borderRadius: 1.5,
                bgcolor: "#f5f3ff",
                color: "#7c3aed",
                textTransform: "none",
                fontSize: "0.84rem",
                fontWeight: 600,
                "&:hover": { bgcolor: "#ede9fe" },
              }}
            >
              Edit Supplier Details
            </Button>
          </CardContent>
          <IconButton
            onClick={(event) => handleMenuClick(event, item)}
            aria-label="action"
            sx={{
              position: "absolute",
              right: 10,
              top: 20,
              color: "#64748b",
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open && selectedItem === item}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 180,
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
                "& .MuiMenuItem-root": { mx: 0.75, borderRadius: 1.5 },
              },
            }}
          >
            <MenuItem
              onClick={() => handleEditItem(item)}
              sx={{ color: "#7c3aed" }}
            >
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              Edit Item
            </MenuItem>
            <MenuItem
              onClick={() => handleDeleteItem(item)}
              sx={{ color: "#dc2626" }}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              Delete Item
            </MenuItem>
          </Menu>
        </Card>
        ))}
      </Box>
    </Box>
  );
};

export default SupplierItems;
